import { useEffect, useState } from "react";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { stripePromise } from "@/lib/stripe";
import { QRCodeSVG } from "qrcode.react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Banknote, Landmark, CreditCard, Link2, Wallet, Copy, MoreVertical, RefreshCw, Mail, XCircle, RotateCcw, Eye, MessageSquare, ExternalLink, Trash2 } from "lucide-react";
import { CHART_TRACK, BLUE, INK } from "@/lib/palette";

/* ---------- Partage d'un lien de paiement (lien + QR + email + SMS) ---------- */
function LinkShare({ link, paymentId, patientEmail, patientPhone }) {
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState(patientEmail || "");
  const [sending, setSending] = useState(false);
  const [showSms, setShowSms] = useState(false);
  const [phone, setPhone] = useState(patientPhone || "");
  const [sendingSms, setSendingSms] = useState(false);
  const sendByEmail = async () => {
    if (!email) { toast.error("Saisissez une adresse email"); return; }
    setSending(true);
    try { await api.post(`/payments/${paymentId}/send-link`, { email }); toast.success("Lien envoyé par email"); setShowEmail(false); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSending(false);
  };
  const sendBySms = async () => {
    if (!phone) { toast.error("Saisissez un numéro de téléphone"); return; }
    setSendingSms(true);
    try { const { data } = await api.post(`/payments/${paymentId}/send-link`, { phone }); if (data.sms_sent) toast.success("Lien envoyé par SMS"); setShowSms(false); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSendingSms(false);
  };
  return (
    <div className="space-y-3 min-w-0 w-full">
      <div className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 bg-slate-50 min-w-0">
        <a href={link} target="_blank" rel="noreferrer" data-testid="pay-link-anchor" className="flex-1 min-w-0 truncate text-xs text-blue-600 underline">{link}</a>
        <button data-testid="pay-link-copy" onClick={() => { navigator.clipboard.writeText(link); toast.success("Lien copié"); }} className="shrink-0 text-slate-500 hover:text-slate-800"><Copy className="w-4 h-4" /></button>
        <a href={link} target="_blank" rel="noreferrer" data-testid="pay-link-open" className="shrink-0 text-slate-500 hover:text-slate-800"><ExternalLink className="w-4 h-4" /></a>
      </div>
      <div className="flex justify-center min-w-0 overflow-hidden" data-testid="pay-link-qr"><div className="p-3 bg-white rounded-lg border border-slate-200"><QRCodeSVG value={link} size={132} /></div></div>
      <div className="flex items-center gap-4">
        <button data-testid="pay-link-email-btn" onClick={() => setShowEmail((v) => !v)} className="inline-flex items-center gap-1.5 text-sm text-blue-600 font-medium"><Mail className="w-4 h-4" />Envoyer par email</button>
        <button data-testid="pay-link-sms-btn" onClick={() => setShowSms((v) => !v)} className="inline-flex items-center gap-1.5 text-sm text-blue-600 font-medium"><MessageSquare className="w-4 h-4" />Envoyer par SMS</button>
      </div>
      {showEmail && (
        <div className="flex items-center gap-2">
          <input data-testid="pay-link-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@exemple.com" className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <button data-testid="pay-link-email-send" disabled={sending} onClick={sendByEmail} className="shrink-0 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50">{sending ? "…" : "Envoyer"}</button>
        </div>
      )}
      {showSms && (
        <div className="flex items-center gap-2">
          <input data-testid="pay-link-sms-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+33 6 12 34 56 78" className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <button data-testid="pay-link-sms-send" disabled={sendingSms} onClick={sendBySms} className="shrink-0 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50">{sendingSms ? "…" : "Envoyer"}</button>
        </div>
      )}
    </div>
  );
}

const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const STATUT_STYLE = {
  "payé": "bg-emerald-100 text-emerald-700",
  "en attente": "bg-amber-100 text-amber-700",
  "échoué": "bg-red-100 text-red-700",
  "annulé": "bg-slate-200 text-slate-600",
  "remboursé": "bg-indigo-100 text-indigo-700",
};
const STATUT_LABEL = { "payé": "Payé", "en attente": "En attente", "échoué": "Échec", "annulé": "Annulé", "remboursé": "Remboursé" };
const MOYEN_LABEL = { "espèces": "Espèces", "chèque": "Chèque", "virement": "Virement bancaire", "CB": "Carte de crédit", "lien": "Lien de paiement" };

function Donut({ paye, total }) {
  const pct = total > 0 ? Math.min(paye / total, 1) : 0;
  const r = 52, c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 140 140" className="w-36 h-36">
      <circle cx="70" cy="70" r={r} fill="none" stroke={CHART_TRACK} strokeWidth="16" />
      <circle cx="70" cy="70" r={r} fill="none" stroke={BLUE} strokeWidth="16"
        strokeDasharray={`${c * pct} ${c}`} strokeLinecap="round" transform="rotate(-90 70 70)" />
    </svg>
  );
}

export default function PaiementsTab({ soin, summary, patientEmail, patientPhone, onChange }) {
  const [payments, setPayments] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refunding, setRefunding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadPayments = () => api.get(`/payments?soin_id=${soin.id}`).then((r) => setPayments(r.data)).catch(() => {});
  useEffect(() => { loadPayments(); /* eslint-disable-next-line */ }, [soin.id]);

  const total = summary?.total_ttc || 0;
  const paye = summary?.total_paye || 0;
  const du = summary?.solde || 0;
  const globalStatut = total <= 0 ? "—" : paye >= total ? "PAYÉ" : paye > 0 ? "PARTIELLEMENT PAYÉ" : "NON PAYÉ";

  const refresh = () => { loadPayments(); onChange?.(); };

  const refreshAll = async () => {
    setRefreshing(true);
    const pend = payments.filter((p) => p.moyen === "lien" && p.statut === "en attente" && p.session_id);
    await Promise.all(pend.map((p) => api.get(`/payments/status/${p.session_id}`).catch(() => {})));
    await loadPayments(); onChange?.();
    setRefreshing(false);
    toast.success("Liste et statuts actualisés");
  };

  const doCancel = async (p) => {
    setMenuId(null);
    try { await api.patch(`/payments/${p.id}`, { statut: "annulé" }); toast.success("Paiement annulé"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doRefund = async () => {
    setRefunding(true);
    try { await api.post(`/payments/${refundTarget.id}/refund`); toast.success("Paiement remboursé"); setRefundTarget(null); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setRefunding(false);
  };
  const doDelete = async () => {
    setDeleting(true);
    try { await api.delete(`/payments/${deleteTarget.id}`); toast.success("Paiement supprimé"); setDeleteTarget(null); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setDeleting(false);
  };

  return (
    <div className="space-y-4" data-testid="fs-paiements-panel">
      <div className="bg-white sx-card p-6 flex flex-wrap items-center justify-between gap-6">
        <div>
          <span data-testid="pay-global-statut" className={`inline-block px-3 py-1.5 rounded-md text-sm font-bold ${paye >= total && total > 0 ? "bg-emerald-100 text-emerald-800" : paye > 0 ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700"}`}>{globalStatut}</span>
          <div className="mt-4 text-xs uppercase tracking-wider text-slate-400">Montant total</div>
          <div className="text-2xl font-bold text-slate-900" data-testid="pay-total">{eur(total)}</div>
        </div>
        <Donut paye={paye} total={total} />
        <div className="space-y-3">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400"><span className="w-2.5 h-2.5 rounded-full bg-blue-600 inline-block" />Déjà payé</div>
            <div className="text-2xl font-bold text-slate-900" data-testid="pay-paye">{eur(paye)}</div>
          </div>
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400"><span className="w-2.5 h-2.5 rounded-full bg-slate-300 inline-block" />Montant dû</div>
            <div className="text-2xl font-bold text-slate-900" data-testid="pay-du">{eur(du)}</div>
          </div>
        </div>
      </div>

      <div className="bg-white sx-card">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-display text-lg font-bold text-slate-900">Paiements</h3>
          <div className="flex items-center gap-2">
            <button data-testid="pay-refresh-btn" onClick={refreshAll} disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />Rafraîchir
            </button>
            <button data-testid="pay-create-btn" onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors">
              Créer un paiement
            </button>
          </div>
        </div>
        {payments.length === 0 ? (
          <div className="px-5 py-8 text-sm text-slate-400 text-center">Aucun paiement enregistré.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="px-5 py-3 font-semibold">Date</th>
                <th className="px-5 py-3 font-semibold">Type</th>
                <th className="px-5 py-3 font-semibold">Notes</th>
                <th className="px-5 py-3 font-semibold">Statut</th>
                <th className="px-5 py-3 font-semibold text-right">Montant</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} data-testid={`pay-row-${p.id}`} onClick={() => setDetail(p)}
                  className="border-b border-slate-50 last:border-0 hover:bg-slate-50 cursor-pointer">
                  <td className="px-5 py-3 text-slate-600">{p.created_at ? new Date(p.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                  <td className="px-5 py-3 text-slate-900 font-medium">{MOYEN_LABEL[p.moyen] || p.moyen}</td>
                  <td className="px-5 py-3 text-slate-400">{p.note || "—"}</td>
                  <td className="px-5 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUT_STYLE[p.statut] || "bg-slate-100 text-slate-600"}`}>{STATUT_LABEL[p.statut] || p.statut}</span></td>
                  <td className="px-5 py-3 text-right font-semibold text-slate-900">{eur(p.montant)}</td>
                  <td className="px-5 py-3 text-slate-300 relative" onClick={(e) => e.stopPropagation()}>
                    <button data-testid={`pay-menu-${p.id}`} onClick={() => setMenuId(menuId === p.id ? null : p.id)} className="hover:text-slate-600"><MoreVertical className="w-4 h-4" /></button>
                    {menuId === p.id && (
                      <div className="absolute right-6 top-9 z-30 w-44 bg-white border border-slate-200 rounded-lg shadow-lg py-1 text-sm">
                        <button data-testid={`pay-detail-open-${p.id}`} onClick={() => { setDetail(p); setMenuId(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-slate-600 hover:bg-slate-50 text-left"><Eye className="w-4 h-4" />Voir le détail</button>
                        {p.statut === "en attente" && <button data-testid={`pay-cancel-${p.id}`} onClick={() => doCancel(p)} className="w-full flex items-center gap-2 px-3 py-2 text-slate-700 hover:bg-slate-50 text-left"><XCircle className="w-4 h-4" />Annuler</button>}
                        {p.statut === "en attente" && <button data-testid={`pay-delete-${p.id}`} onClick={() => { setMenuId(null); setDeleteTarget(p); }} className="w-full flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-slate-50 text-left"><Trash2 className="w-4 h-4" />Supprimer</button>}
                        {["CB", "lien"].includes(p.moyen) && p.statut === "payé" && <button data-testid={`pay-refund-${p.id}`} onClick={() => { setMenuId(null); setRefundTarget(p); }} className="w-full flex items-center gap-2 px-3 py-2 text-indigo-600 hover:bg-slate-50 text-left"><RotateCcw className="w-4 h-4" />Rembourser</button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {menuId && <div className="fixed inset-0 z-20" onClick={() => setMenuId(null)} />}
      {createOpen && <CreatePaymentDialog soin={soin} solde={du} patientEmail={patientEmail} patientPhone={patientPhone} onClose={() => setCreateOpen(false)} onRefresh={refresh} onDone={() => { setCreateOpen(false); refresh(); }} />}
      {detail && <PaymentDetailDialog payment={detail} patientEmail={patientEmail} patientPhone={patientPhone} onClose={() => setDetail(null)} onSaved={() => { setDetail(null); refresh(); }} />}
      <Dialog open={!!refundTarget} onOpenChange={(o) => !o && setRefundTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmer le remboursement</DialogTitle>
            <DialogDescription>Vous êtes sur le point de rembourser {refundTarget ? eur(refundTarget.montant) : ""} via Stripe. Cette action est définitive et le paiement passera au statut « Remboursé ».</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button onClick={() => setRefundTarget(null)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
            <button data-testid="pay-refund-confirm" disabled={refunding} onClick={doRefund} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50">{refunding ? "Remboursement…" : "Rembourser"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Supprimer ce paiement ?</DialogTitle>
            <DialogDescription>Le paiement en attente de {deleteTarget ? eur(deleteTarget.montant) : ""} ({deleteTarget ? (MOYEN_LABEL[deleteTarget.moyen] || deleteTarget.moyen) : ""}) sera définitivement supprimé.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
            <button data-testid="pay-delete-confirm" disabled={deleting} onClick={doDelete} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium disabled:opacity-50">{deleting ? "Suppression…" : "Supprimer"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Fenêtre "Créer un paiement" ---------- */
const METHODS = [
  { key: "espèces", label: "Espèces", icon: Wallet },
  { key: "chèque", label: "Chèque", icon: Banknote },
  { key: "virement", label: "Virement bancaire", icon: Landmark },
  { key: "CB", label: "Carte de crédit", icon: CreditCard },
  { key: "lien", label: "Lien de paiement", icon: Link2 },
];

function CreatePaymentDialog({ soin, solde, patientEmail, patientPhone, onClose, onRefresh, onDone }) {
  const [montant, setMontant] = useState(solde > 0 ? solde : 0);
  const [moyen, setMoyen] = useState("espèces");
  const [statut, setStatut] = useState("en attente");
  const [busy, setBusy] = useState(false);

  const submitManual = async () => {
    setBusy(true);
    try {
      const finalStatut = moyen === "espèces" ? "payé" : statut;
      await api.post("/payments", { soin_id: soin.id, patient_id: soin.patient_id, rdv_id: soin.rdv_id, moyen, montant: Number(montant), statut: finalStatut });
      toast.success("Paiement enregistré");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Créer un paiement</DialogTitle>
          <DialogDescription>Sélectionnez un mode de paiement et le montant à encaisser pour ce soin.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-5 py-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">Montant à encaisser</div>
            <div className="text-xs text-slate-400 mt-0.5">Solde dû : {eur(solde)}</div>
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="pay-montant-input" type="number" step="0.01" value={montant} onChange={(e) => setMontant(e.target.value)}
              className="w-36 text-right px-3 py-2 rounded-lg border border-slate-300 text-xl font-bold focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none" />
            <span className="text-xl font-bold text-slate-900">€</span>
          </div>
        </div>

        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2">Sélectionner un mode de paiement</div>
          <div className="flex flex-wrap gap-2">
            {METHODS.map(({ key, label, icon: Icon }) => (
              <button key={key} data-testid={`pay-method-${key}`} onClick={() => setMoyen(key)}
                className={`inline-flex items-center gap-2 px-3.5 py-2.5 rounded-lg border text-sm font-medium transition-colors ${moyen === key ? "bg-blue-50 border-blue-400 text-blue-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                <Icon className="w-4 h-4" />{label}
              </button>
            ))}
          </div>
        </div>

        <div className="pt-1 min-w-0">
          {(moyen === "chèque" || moyen === "virement") && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Statut du paiement</label>
              <select data-testid="pay-statut-select" value={statut} onChange={(e) => setStatut(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm">
                <option value="en attente">En attente</option>
                <option value="payé">Payé</option>
              </select>
              <DialogFooter className="pt-2">
                <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
                <button data-testid="pay-submit-manual" disabled={busy} onClick={submitManual} className="px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium disabled:opacity-50">Enregistrer</button>
              </DialogFooter>
            </div>
          )}
          {moyen === "espèces" && (
            <div className="space-y-2">
              <p className="text-sm text-slate-500">Encaissement en espèces — le statut sera <strong>Payé</strong>.</p>
              <DialogFooter className="pt-2">
                <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
                <button data-testid="pay-submit-manual" disabled={busy} onClick={submitManual} className="px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium disabled:opacity-50">Encaisser</button>
              </DialogFooter>
            </div>
          )}
          {moyen === "CB" && (
            <Elements stripe={stripePromise}>
              <CardSection soin={soin} montant={montant} onDone={onDone} onClose={onClose} />
            </Elements>
          )}
          {moyen === "lien" && <LinkSection soin={soin} montant={montant} patientEmail={patientEmail} patientPhone={patientPhone} onClose={onClose} onRefresh={onRefresh} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Section carte de crédit (Stripe) ---------- */
function CardSection({ soin, montant, onDone, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [cards, setCards] = useState([]);
  const [useNew, setUseNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState("");
  const [saveCard, setSaveCard] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!soin.patient_id) { setUseNew(true); return; }
    api.get(`/patients/${soin.patient_id}/cards`).then((r) => {
      setCards(r.data); setUseNew(r.data.length === 0);
      if (r.data[0]) setSelected(r.data[0].id);
    }).catch(() => setUseNew(true));
  }, [soin.patient_id]);

  const finish = (data) => {
    if (data.status === "succeeded") toast.success("Paiement encaissé");
    else toast.info("Paiement en cours de confirmation");
    onDone();
  };

  const charge = async () => {
    if (Number(montant) <= 0) { toast.error("Montant invalide"); return; }
    setBusy(true);
    try {
      if (useNew) {
        if (!stripe || !elements) { toast.error("Stripe non chargé"); setBusy(false); return; }
        const card = elements.getElement(CardElement);
        const { error, paymentMethod } = await stripe.createPaymentMethod({ type: "card", card, billing_details: { name } });
        if (error) { toast.error(error.message); setBusy(false); return; }
        const { data } = await api.post("/payments/card", { soin_id: soin.id, patient_id: soin.patient_id, montant: Number(montant), payment_method_id: paymentMethod.id, save_card: saveCard });
        finish(data);
      } else {
        const { data } = await api.post("/payments/card", { soin_id: soin.id, patient_id: soin.patient_id, montant: Number(montant), payment_method_id: selected, use_saved: true });
        finish(data);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="space-y-3">
      {cards.length > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700">Cartes enregistrées</span>
          <button onClick={() => setUseNew((v) => !v)} className="text-sm text-blue-600 font-medium">
            {useNew ? "Utiliser une carte enregistrée" : "+ Utiliser une nouvelle carte"}
          </button>
        </div>
      )}
      {!useNew && cards.map((c) => (
        <label key={c.id} data-testid={`pay-saved-card-${c.last4}`}
          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${selected === c.id ? "border-blue-400 bg-blue-50" : "border-slate-200"}`}>
          <input type="radio" checked={selected === c.id} onChange={() => setSelected(c.id)} />
          <CreditCard className="w-5 h-5 text-slate-400" />
          <span className="text-sm text-slate-700">{c.brand} •••• {c.last4} — exp {c.exp_month}/{c.exp_year}</span>
        </label>
      ))}
      {useNew && (
        <>
          <input data-testid="pay-card-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom sur la carte"
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <div className="px-3 py-3 rounded-lg border border-slate-300">
            <CardElement options={{ style: { base: { fontSize: "15px", color: INK } } }} />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)} />
            Mémoriser cette carte pour ce patient
          </label>
        </>
      )}
      <DialogFooter className="pt-2">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
        <button data-testid="pay-charge-card" disabled={busy || (!useNew && !selected)} onClick={charge}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50">
          {busy ? "Traitement…" : "Charger la carte"}
        </button>
      </DialogFooter>
    </div>
  );
}

/* ---------- Section lien de paiement ---------- */
function LinkSection({ soin, montant, patientEmail, patientPhone, onClose, onRefresh }) {
  const [link, setLink] = useState("");
  const [paymentId, setPaymentId] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (Number(montant) <= 0) { toast.error("Montant invalide"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/link", {
        soin_id: soin.id, patient_id: soin.patient_id, rdv_id: soin.rdv_id,
        montant: Number(montant), origin_url: window.location.origin,
        libelle: "Soin ostéopathie", send_email: false,
      });
      setLink(data.checkout_url); setPaymentId(data.payment_id);
      toast.success("Lien de paiement généré");
      onRefresh?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (!link) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-slate-500">Générez un lien de paiement sécurisé Stripe pour le montant indiqué.</p>
        <DialogFooter className="pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
          <button data-testid="pay-generate-link" disabled={busy} onClick={generate} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50">
            {busy ? "…" : "Générer le lien"}
          </button>
        </DialogFooter>
      </div>
    );
  }

  return (
    <div className="space-y-3 min-w-0">
      <LinkShare link={link} paymentId={paymentId} patientEmail={patientEmail} patientPhone={patientPhone} />
      <DialogFooter className="pt-2">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Fermer</button>
      </DialogFooter>
    </div>
  );
}

/* ---------- Détail paiement + changement de statut ---------- */
function PaymentDetailDialog({ payment, patientEmail, patientPhone, onClose, onSaved }) {
  const [statut, setStatut] = useState(payment.statut);
  const [busy, setBusy] = useState(false);
  const editable = ["virement", "chèque"].includes(payment.moyen) && !payment.lecture_seule;

  const save = async () => {
    setBusy(true);
    try { await api.patch(`/payments/${payment.id}`, { statut }); toast.success("Statut mis à jour"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg overflow-hidden">
        <DialogHeader>
          <DialogTitle>Détail du paiement</DialogTitle>
          <DialogDescription>{payment.id}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm min-w-0">
          <div className="flex justify-between"><span className="text-slate-500">Type</span><span className="font-medium">{MOYEN_LABEL[payment.moyen] || payment.moyen}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Montant</span><span className="font-medium">{eur(payment.montant)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Date</span><span className="font-medium">{payment.created_at ? new Date(payment.created_at).toLocaleString("fr-FR") : "—"}</span></div>
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <span className="text-slate-500">Statut</span>
            {editable ? (
              <select data-testid="pay-detail-statut" value={statut} onChange={(e) => setStatut(e.target.value)} className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm">
                <option value="en attente">En attente</option>
                <option value="payé">Payé</option>
                <option value="échoué">Échec</option>
                <option value="annulé">Annulé</option>
              </select>
            ) : <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUT_STYLE[payment.statut]}`}>{STATUT_LABEL[payment.statut] || payment.statut}</span>}
          </div>
        </div>
        {payment.moyen === "lien" && payment.lien_paiement && (
          <div className="pt-3 border-t border-slate-100 min-w-0" data-testid="pay-detail-link">
            <div className="text-sm font-semibold text-slate-700 mb-2">Lien de paiement</div>
            <LinkShare link={payment.lien_paiement} paymentId={payment.id} patientEmail={patientEmail} patientPhone={patientPhone} />
          </div>
        )}
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Fermer</button>
          {editable && <button data-testid="pay-detail-save" disabled={busy} onClick={save} className="px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium disabled:opacity-50">Enregistrer</button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
